import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isCompanyPayrollWindowOpen, numOrDefault } from '@/lib/invoiceHelpers';

// Draws a titled, itemized table with a dark header row and zebra
// striping -- reused for both the Earnings Summary and Deductions
// tables. Returns the y position after the table (and after paging).
function drawTable(pdf, x0, y0, cols, rows, title, pageH) {
  let y = y0;
  const totalW = cols.reduce((s, c) => s + c.w, 0);

  pdf.setFontSize(10); pdf.setFont(undefined, 'bold'); pdf.setTextColor('#111111');
  pdf.text(title, x0, y); y += 6;
  pdf.setDrawColor(180, 180, 180); pdf.line(x0, y, x0 + totalW, y); y += 14;

  const drawHeader = () => {
    pdf.setFillColor(30, 30, 30);
    pdf.rect(x0, y - 10, totalW, 14, 'F');
    pdf.setFontSize(7); pdf.setFont(undefined, 'bold'); pdf.setTextColor('#ffffff');
    let cx = x0;
    for (const c of cols) {
      pdf.text(c.label, c.align === 'right' ? cx + c.w - 4 : cx + 4, y, { align: c.align === 'right' ? 'right' : 'left' });
      cx += c.w;
    }
    pdf.setTextColor('#111111');
    y += 12;
  };
  drawHeader();

  pdf.setFontSize(7); pdf.setFont(undefined, 'normal');
  rows.forEach((cells, rowIdx) => {
    if (y + 14 > pageH - 40) { pdf.addPage(); y = 40; drawHeader(); pdf.setFontSize(7); pdf.setFont(undefined, 'normal'); }
    if (rowIdx % 2 === 1) {
      pdf.setFillColor(248, 248, 248);
      pdf.rect(x0, y - 9, totalW, 13, 'F');
    }
    let cx = x0;
    cells.forEach((val, i) => {
      const c = cols[i];
      const text = pdf.splitTextToSize(String(val ?? ''), c.w - 8)[0] || '';
      pdf.text(text, c.align === 'right' ? cx + c.w - 4 : cx + 4, y, { align: c.align === 'right' ? 'right' : 'left' });
      cx += c.w;
    });
    y += 13;
  });

  if (rows.length === 0) {
    pdf.setFont(undefined, 'italic'); pdf.setTextColor('#888888');
    pdf.text('No entries', x0 + 4, y);
    pdf.setTextColor('#111111');
    y += 13;
  }
  return y;
}

// Pay statement PDF matching the carrier's reference pay-stub template:
// company + statement-info header, a highlighted Net/Gross/Deductions
// summary bar, employee info, an itemized Earnings Summary table (one
// row per load's line-haul pay, plus one row per manual
// SettlementLineItem earning), and an itemized Deductions table (per-load
// carrier/dispatch/fuel-advance/lumper/late fees + manual
// SettlementLineItem deductions + weekly fixed costs as their own line).
// Called once per settlement for Owner Operators (one statement per
// load) and once for the whole batch for Company Drivers (one combined
// statement per pay period) -- see handleSubmit below.
async function generatePayStatementPDF({
  driver, profile, settlements, allLineItems, loadsById, assets, weeklyFixed,
  paymentRef, paymentDate, periodStart, periodEnd, statementNumber,
}) {
  try {
    const companySettings = await client.entities.CompanySettings.list().catch(() => []);
    const getSetting = (key) => companySettings.find(s => s.key === key)?.value || '';
    const companyName = getSetting('company_name') || 'Carrier Portal';
    const companyAddress = getSetting('company_address');
    const companyCityState = getSetting('company_city_state');

    const findTruck = (email) =>
      assets.find(a => a.assigned_driver_email?.toLowerCase() === email?.toLowerCase())?.unit_number || '';

    const fmt = (n) => '$' + Math.abs(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // ── Earnings: one row per load's line haul + one per manual earning item ──
    const earningsRows = [];
    let grossTotal = 0;
    let totalMiles = 0;
    for (const s of settlements) {
      const load = loadsById[s.load_id];
      const gross = s.gross_revenue || 0;
      grossTotal += gross;
      const miles = load?.miles ? Number(load.miles) : null;
      if (miles) totalMiles += miles;
      const truck = findTruck(s.driver_email);
      earningsRows.push([
        s.settlement_date || load?.delivery_date || '',
        s.load_number || '',
        'Line Haul',
        miles ? miles.toLocaleString() : '—',
        miles ? miles.toLocaleString() : '1',
        miles ? fmt(gross / miles) + '/mi' : fmt(gross),
        fmt(gross),
        truck || '—',
        load?.origin || '',
        load?.destination || '',
      ]);
      for (const item of (allLineItems[s.id] || []).filter(i => i.type === 'earning')) {
        const amt = Math.abs(item.amount || 0);
        grossTotal += amt;
        earningsRows.push([
          s.settlement_date || '', s.load_number || '',
          [item.category, item.memo].filter(Boolean).join(' — '),
          '—', '—', '—', fmt(amt), truck || '—', '', '',
        ]);
      }
    }

    // ── Deductions: per-load fees + manual deduction items + weekly fixed ──
    const deductionRows = [];
    let deductionsTotal = 0;
    for (const s of settlements) {
      const carrierFee = numOrDefault(s.carrier_take_amount, (s.gross_revenue || 0) * 0.15);
      const pushDeduction = (desc, amt) => {
        if (!(amt > 0)) return;
        deductionsTotal += amt;
        deductionRows.push([s.settlement_date || '', s.load_number || '', desc, fmt(amt), '']);
      };
      pushDeduction(`Carrier Fee (${numOrDefault(s.carrier_take_percent, 15)}%)`, carrierFee);
      pushDeduction(`Dispatch Fee (${numOrDefault(s.dispatch_fee_percent, 3)}%)`, s.dispatch_fee_amount);
      pushDeduction('Fuel Advance Repayment', s.fuel_advance_total);
      pushDeduction('Lumper Fee', s.lumper_fee_deducted);
      pushDeduction('Late Fee', s.late_fee_deducted);
      for (const item of (allLineItems[s.id] || []).filter(i => i.type === 'deduction')) {
        pushDeduction([item.category, item.memo].filter(Boolean).join(' — '), Math.abs(item.amount || 0));
      }
    }
    if (weeklyFixed > 0) {
      deductionsTotal += weeklyFixed;
      deductionRows.push(['', '', 'Weekly Fixed Costs (Insurance, ELD, Truck/Trailer Payments)', fmt(weeklyFixed), '']);
    }

    const netEarnings = grossTotal - deductionsTotal;

    const { jsPDF } = await import('jspdf');
    const pdf = new jsPDF({ format: 'letter', orientation: 'landscape', unit: 'pt' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const marginL = 36;
    const marginR = pageW - 36;
    let y = 40;

    // ── Header: company block (left) + statement info box (right) ──
    pdf.setFontSize(16); pdf.setFont(undefined, 'bold'); pdf.setTextColor('#111111');
    pdf.text(companyName, marginL, y);
    pdf.setFontSize(9); pdf.setFont(undefined, 'normal');
    const addrLine = [companyAddress, companyCityState].filter(Boolean).join(', ');
    if (addrLine) pdf.text(addrLine, marginL, y + 14);

    const boxW = 230, boxX = marginR - boxW, boxY = y - 24;
    pdf.setDrawColor(200, 200, 200); pdf.rect(boxX, boxY, boxW, 76);
    pdf.setFontSize(10); pdf.setFont(undefined, 'bold');
    pdf.text('EARNINGS & DEDUCTIONS STATEMENT', boxX + boxW / 2, boxY + 14, { align: 'center' });
    pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
    const infoRows = [
      ['Pay Stub Number:', statementNumber],
      ['Pay Period Start:', periodStart || '—'],
      ['Pay Period End:', periodEnd || '—'],
      ['Payment Date:', paymentDate],
    ];
    let iy = boxY + 28;
    for (const [label, val] of infoRows) {
      pdf.text(label, boxX + 8, iy);
      pdf.text(String(val), boxX + boxW - 8, iy, { align: 'right' });
      iy += 12;
    }

    y = Math.max(y + 40, boxY + 86);

    // ── Net/Gross/Deductions summary bar ──
    pdf.setFillColor(245, 245, 245);
    pdf.rect(marginL, y, marginR - marginL, 46, 'F');
    pdf.setFontSize(9); pdf.setFont(undefined, 'bold');
    pdf.text('NET EARNINGS FOR THIS PERIOD', marginL + 10, y + 18);
    pdf.setFontSize(15);
    pdf.text(fmt(netEarnings), marginR - 10, y + 20, { align: 'right' });
    pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
    pdf.text(`Gross Earnings: ${fmt(grossTotal)}     Total Deductions: ${fmt(deductionsTotal)}`, marginL + 10, y + 36);
    y += 62;

    // ── Employee info -- prefer the driver's on-file profile name/address
    // over driver.name, which is just a settlement-time snapshot that
    // falls back to the email when full_name was never set at signup ──
    const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim()
      || profile?.full_name
      || (driver.name && driver.name !== driver.email ? driver.name : '')
      || driver.email;
    const addressLine1 = profile?.address_street || '';
    const addressLine2 = [
      [profile?.address_city, profile?.address_state].filter(Boolean).join(', '),
      profile?.address_zip,
    ].filter(Boolean).join(' ');
    const infoLines = [];
    // Company Name is an Owner Operator's own business entity -- checked
    // against driver_type here too (not just truthiness) so a driver
    // switched to Company Driver never shows a stale "DBA: <old
    // company>" line just because that field hasn't been cleared on
    // their profile yet (DriverFormModal.jsx now clears it going
    // forward, but this guards existing records regardless).
    if (profile?.company_name && profile?.driver_type !== 'Company Driver') infoLines.push(`DBA: ${profile.company_name}`);
    if (addressLine1) infoLines.push(addressLine1);
    if (addressLine2) infoLines.push(addressLine2);
    infoLines.push(driver.email);
    if (profile?.phone) infoLines.push(profile.phone);

    pdf.setFontSize(10); pdf.setFont(undefined, 'bold');
    pdf.text(fullName, marginL, y);
    pdf.setFont(undefined, 'normal'); pdf.setFontSize(9);
    let infoY = y + 14;
    for (const line of infoLines) {
      pdf.text(line, marginL, infoY);
      infoY += 13;
    }
    y = infoY + 10;

    // ── Earnings Summary table ──
    const earnCols = [
      { label: 'Work Date', w: 60 },
      { label: 'Order #', w: 55 },
      { label: 'Description', w: 130 },
      { label: 'Miles', w: 45, align: 'right' },
      { label: 'Qty', w: 45, align: 'right' },
      { label: 'Rate', w: 55, align: 'right' },
      { label: 'Earnings', w: 60, align: 'right' },
      { label: 'Truck #', w: 45 },
      { label: 'Start', w: 90 },
      { label: 'End', w: 90 },
    ];
    y = drawTable(pdf, marginL, y, earnCols, earningsRows, 'EARNINGS SUMMARY', pageH);
    y += 6;
    pdf.setFontSize(9); pdf.setFont(undefined, 'bold');
    pdf.text(`TOTAL MILES: ${totalMiles.toLocaleString()}`, marginL, y);
    pdf.text(`GROSS EARNINGS: ${fmt(grossTotal)}`, marginR, y, { align: 'right' });
    y += 24;

    // ── Deductions table ──
    if (y + 60 > pageH - 40) { pdf.addPage(); y = 40; }
    const dedCols = [
      { label: 'Work Date', w: 70 },
      { label: 'Order #', w: 70 },
      { label: 'Description', w: 340 },
      { label: 'Amount', w: 80, align: 'right' },
      { label: 'Details', w: 115 },
    ];
    y = drawTable(pdf, marginL, y, dedCols, deductionRows, 'DEDUCTIONS', pageH);
    y += 6;
    pdf.setFontSize(9); pdf.setFont(undefined, 'bold');
    pdf.text(`TOTAL DEDUCTIONS: ${fmt(deductionsTotal)}`, marginR, y, { align: 'right' });
    y += 24;

    if (y + 20 > pageH - 40) { pdf.addPage(); y = 40; }
    pdf.setFontSize(7); pdf.setFont(undefined, 'italic'); pdf.setTextColor('#888888');
    pdf.text(`Generated by ${companyName} on ${new Date().toISOString().split('T')[0]}${paymentRef ? ` — Ref: ${paymentRef}` : ''}`, marginL, y);

    const pdfBlob = pdf.output('blob');
    const loadNums = settlements.map(s => s.load_number).join('-');
    const pdfFile = new File(
      [pdfBlob],
      `pay-statement-${driver.name.replace(/\s+/g, '_')}-${loadNums}-${paymentDate}.pdf`,
      { type: 'application/pdf' }
    );
    const { file_url } = await client.integrations.Core.UploadFile({ file: pdfFile });
    return file_url;
  } catch (err) {
    console.error('PDF generation error:', err);
    return null;
  }
}

export default function RecordPaymentModal({ driver, settlements, personalAdvances = [], weeklyFixed = 0, onClose, onPaid }) {
  const today = new Date().toISOString().split('T')[0];
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentDate, setPaymentDate] = useState(today);
  const [submitting, setSubmitting] = useState(false);
  const [companyName, setCompanyName] = useState('Carrier Portal');
  const [driverType, setDriverType] = useState('Company Driver');
  const [driverProfile, setDriverProfile] = useState(null);

  useEffect(() => {
    client.entities.CompanySettings.list().then(settings => {
      const name = settings.find(s => s.key === 'company_name')?.value;
      if (name) setCompanyName(name);
    }).catch(() => {});
    client.entities.DriverProfile.filter({ user_email: driver.email }, '-updated_date', 1).then(profiles => {
      if (profiles[0]) setDriverProfile(profiles[0]);
      if (profiles[0]?.driver_type) setDriverType(profiles[0].driver_type);
    }).catch(() => {});
  }, [driver.email]);

  // final_net_pay is the per-load net set at approval (includes all per-load deductions + manual adjustments).
  // Weekly fixed (insurance + ELD) are subtracted once here at payroll time.
  const loadsTotal = settlements.reduce((sum, s) => sum + (s.final_net_pay ?? s.net_driver_pay ?? 0), 0);
  const totalDue = Math.max(0, loadsTotal - weeklyFixed);
  // Owner Operators get paid per load (see computePayDueAt) -- so they
  // get one statement per load even when a batch payment covers several,
  // instead of Company Drivers' single combined weekly statement.
  const isOwnerOperator = driverType === 'Owner Operator';
  // Company driver payroll only opens Wed-Sat each week -- Owner
  // Operators are paid per load (computePayDueAt) and aren't gated by
  // this weekly window.
  const payrollWindowOpen = isOwnerOperator || isCompanyPayrollWindowOpen();

  const handleSubmit = async () => {
    if (!paymentRef.trim()) {
      toast.error('Please enter a confirmation / check number.');
      return;
    }
    if (!payrollWindowOpen) {
      toast.error('Company driver payroll can only be submitted Wednesday through Saturday.');
      return;
    }
    setSubmitting(true);
    toast.info('Generating pay statement...');

    const [allLineItemsArr, loadsArr, assets] = await Promise.all([
      Promise.all(settlements.map(s => client.entities.SettlementLineItem.filter({ settlement_id: s.id }).catch(() => []))),
      Promise.all(settlements.map(s => s.load_id ? client.entities.Load.get(s.load_id).catch(() => null) : null)),
      client.entities.Asset.list('-created_date', 200).catch(() => []),
    ]);
    const allLineItems = {};
    settlements.forEach((s, i) => { allLineItems[s.id] = allLineItemsArr[i]; });
    const loadsById = {};
    settlements.forEach((s, i) => { if (loadsArr[i]) loadsById[s.load_id] = loadsArr[i]; });

    const pdfUrls = {}; // settlement.id -> pdf_url

    if (isOwnerOperator) {
      // One statement per load, even when paying multiple at once.
      for (const s of settlements) {
        const url = await generatePayStatementPDF({
          driver, profile: driverProfile, settlements: [s], allLineItems, loadsById, assets,
          weeklyFixed: 0,
          paymentRef: paymentRef.trim(), paymentDate,
          periodStart: s.settlement_date, periodEnd: s.settlement_date,
          statementNumber: s.load_number || s.id.slice(0, 8),
        });
        pdfUrls[s.id] = url;
      }
    } else {
      // One combined statement covering the whole pay period.
      const dates = settlements.map(s => s.settlement_date).filter(Boolean).sort();
      const url = await generatePayStatementPDF({
        driver, profile: driverProfile, settlements, allLineItems, loadsById, assets,
        weeklyFixed,
        paymentRef: paymentRef.trim(), paymentDate,
        periodStart: dates[0] || '', periodEnd: dates[dates.length - 1] || '',
        statementNumber: paymentRef.trim(),
      });
      settlements.forEach(s => { pdfUrls[s.id] = url; });
    }

    // Mark all settlements as Paid with the same ref + their own PDF URL.
    // finalized_date is the real moment accounting hit Submit -- not
    // paymentDate, which is a separate, editable "Payment Date" printed on
    // the statement (accounting can legitimately backdate/postdate that).
    // A single shared timestamp (not one `new Date()` per settlement) keeps
    // every settlement in this batch grouped together by PayrollHistoryTab.
    const finalizedAt = new Date().toISOString();
    await Promise.all(settlements.map(s =>
      client.entities.Settlement.update(s.id, {
        status: 'Paid',
        is_finalized: true,
        payment_reference: paymentRef.trim(),
        finalized_date: finalizedAt,
        ...(pdfUrls[s.id] ? { pdf_url: pdfUrls[s.id] } : {}),
      })
    ));

    // Stamp every personal advance folded into this batch so it's never
    // picked up and deducted again on a future payroll run.
    await Promise.all(personalAdvances.map(a =>
      client.entities.FuelAdvance.update(a.id, { applied_payment_reference: paymentRef.trim() })
    ));

    // One email either way -- for Owner Operators (one PDF per load) every
    // load gets its own link so nothing is left un-sent; Company Drivers
    // get the single combined statement link.
    const loadList = settlements.map(s => `  • Load #${s.load_number} — $${(s.final_net_pay ?? s.net_driver_pay ?? 0).toFixed(2)}`).join('\n');
    let pdfLink = '';
    if (isOwnerOperator) {
      const links = settlements
        .map(s => pdfUrls[s.id] ? `  • Load #${s.load_number}: ${pdfUrls[s.id]}` : null)
        .filter(Boolean)
        .join('\n');
      pdfLink = links ? `\n\nDownload your pay statement${settlements.length !== 1 ? 's' : ''}:\n${links}` : '';
    } else {
      const primaryPdfUrl = pdfUrls[settlements[0]?.id];
      pdfLink = primaryPdfUrl ? `\n\nDownload your pay statement:\n${primaryPdfUrl}` : '';
    }
    await client.integrations.Core.SendEmail({
      to: driver.email,
      subject: `Pay Statement — ${settlements.length} Load${settlements.length !== 1 ? 's' : ''} — Ref: ${paymentRef.trim()}`,
      body: `Hi ${driver.name},\n\nYour payment has been processed for the following loads:\n\n${loadList}\n\nTotal Net Pay: $${totalDue.toFixed(2)}\nPayment Reference: ${paymentRef.trim()}\nPayment Date: ${paymentDate}${pdfLink}\n\nThank you,\n${companyName}`,
    }).catch(() => {});

    toast.success(`Payment recorded for ${driver.name} — Ref: ${paymentRef}`);
    setSubmitting(false);
    onPaid();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="flex min-h-full items-start justify-center p-4">
      <div className="relative w-full max-w-md my-8 z-10 rounded-2xl border border-border bg-background">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div>
            <h2 className="font-space font-bold text-foreground text-lg">Record Payment</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{driver.name} — {settlements.length} settlement{settlements.length !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {!payrollWindowOpen && (
            <p className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
              Company driver payroll opens Wednesday — check back then to submit this payment.
            </p>
          )}

          {/* Statement type note */}
          <p className="text-xs text-muted-foreground bg-muted/40 border border-border rounded-lg px-3 py-2">
            {isOwnerOperator
              ? `Owner Operator — a separate downloadable statement will be generated for each of the ${settlements.length} load${settlements.length !== 1 ? 's' : ''}.`
              : 'Company Driver — one combined statement will be generated for this pay period.'}
          </p>

          {/* Section 1: Per-Load Settlements */}
          <div className="glass-card-dark rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 bg-white/3 border-b border-border">
              <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Section 1 — Per-Load Net Pay</p>
              <p className="text-xs text-muted-foreground mt-0.5">Carrier fee &amp; fuel advances already deducted</p>
            </div>
            <div className="divide-y divide-border">
              {settlements.map(s => {
                const sNet = s.final_net_pay ?? s.net_driver_pay ?? 0;
                return (
                  <div key={s.id} className="px-4 py-3 text-sm flex justify-between items-center">
                    <span className="text-foreground font-medium">Load #{s.load_number}</span>
                    <span className="font-semibold text-green-400">${sNet.toFixed(2)}</span>
                  </div>
                );
              })}
              <div className="flex justify-between items-center px-4 py-2.5 bg-white/3 border-t border-border text-sm">
                <span className="text-muted-foreground font-semibold">Loads Subtotal</span>
                <span className="text-foreground font-semibold">${loadsTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Section 2: Weekly Fixed Deductions */}
          {weeklyFixed > 0 && (
            <div className="glass-card-dark rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 bg-blue-500/5 border-b border-blue-500/20">
                <p className="text-xs font-semibold text-blue-300 uppercase tracking-wide">Section 2 — Weekly Fixed Deductions</p>
                <p className="text-xs text-muted-foreground mt-0.5">Applied once this pay period — not tied to any individual load</p>
              </div>
              <div className="px-4 py-3 text-sm flex justify-between items-center">
                <span className="text-blue-200">Weekly Fixed Deductions (Insurance, ELD, Truck & Trailer Payments)</span>
                <span className="text-red-400 font-semibold">-${weeklyFixed.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Grand Total */}
          <div className="glass-card-dark rounded-xl px-4 py-3 flex justify-between items-center border border-green-500/20 bg-green-500/5">
            <span className="font-bold text-foreground">Net Due to Driver</span>
            <span className="font-bold text-xl text-green-400">${totalDue.toFixed(2)}</span>
          </div>

          {/* Payment Reference */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Confirmation / Check Number <span className="text-red-400">*</span></label>
            <input
              value={paymentRef}
              onChange={e => setPaymentRef(e.target.value)}
              placeholder="e.g. CHK-10042 or ACH-8837291"
              className="w-full px-3 py-2.5 rounded-xl bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>

          {/* Payment Date */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Payment Date</label>
            <input
              type="date"
              value={paymentDate}
              onChange={e => setPaymentDate(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex gap-3 justify-end">
          <Button onClick={onClose} variant="outline" className="border-border" disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || !payrollWindowOpen} className="bg-primary text-primary-foreground min-w-[160px]">
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recording...</>
            ) : (
              <><CreditCard className="w-4 h-4 mr-2" /> Record Payment</>
            )}
          </Button>
        </div>
      </div>
      </div>
    </div>
  );
}
